import { api } from '../api/client';
import { API_ADMIN_INVITES_URL, API_CLONEABLE_GROUPS_URL, API_COLLECTIONS_URL, API_ITEMS_URL } from '../utils';
import { withElementChoicesField } from '../utils/elementRefusal';

// The sharing endpoints are the ONLY write path for groups and grants (the
// collection PUT strips authorization fields server-side). All mutations are
// manage-gated and apply immediately — the Sharing tab saves outside the
// settings form's Save button on purpose.

const sharingUrl = (collectionId, suffix = '') =>
	`${API_COLLECTIONS_URL}/${collectionId}/${suffix}`.replace(/\/$/, '');

/**
 * { owner, groups: [{_id, name, kind, rule, members: [publicUser]}], grants: [...] }
 * A group's `members` is the CURRENT roster: a dynamic group's is resolved
 * live from its rule on every read.
 */
export async function getSharing(collectionId) {
	const { data } = await api.get(sharingUrl(collectionId, 'sharing'));
	return data;
}

/**
 * The rule as the wire carries it: `card_id`, and `q` only when there are
 * conditions. An ABSENT `q` means every linked record of the card (the API
 * reads absent and empty alike), so blank text is left off rather than sent
 * as '' — one shape for "no conditions", whichever way the editor got there.
 */
function ruleBody(rule) {
	const q = typeof rule.q === 'string' ? rule.q.trim() : '';
	return q ? { card_id: rule.card_id, q } : { card_id: rule.card_id };
}

/**
 * Static (the default): a member list. Dynamic (`kind: 'dynamic'`): a
 * `rule { card_id, q? }` and no member list — the kinds are exclusive, so a
 * dynamic create never carries `memberIds` and a static one never carries
 * `rule`, whatever the caller passed. A clone keeps its SOURCE's kind (a
 * `kind` that disagrees is 400 `invalid_kind`), so a cloning caller leaves
 * `kind` undefined and the API answers from the source; extra `memberIds`
 * union into a static clone, a dynamic clone copies the source's rule.
 *
 * 400 codes: `invalid_kind`, `members_on_dynamic_group`, `invalid_rule
 * { field }`, `rule_card_has_no_account_link { card_id }`,
 * `query_me_unsupported { field: 'q' }` (utils/dynamicGroups.placeGroupError).
 */
export async function createGroup(collectionId, { name, kind, memberIds, rule, cloneFromGroupId, elementChoices }) {
	const payload = { name };
	if (kind !== undefined) payload.kind = kind;
	if (kind === 'dynamic') {
		if (rule) payload.rule = ruleBody(rule);
	} else if (memberIds?.length) {
		payload.memberIds = memberIds;
	}
	if (cloneFromGroupId) payload.cloneFromGroupId = cloneFromGroupId;
	// The person's answer to an ambiguous element name (utils/elementRefusal.js).
	const { data } = await api.post(sharingUrl(collectionId, 'groups'), withElementChoicesField(payload, elementChoices));
	return data;
}

/**
 * A partial statement: only the fields given are sent. `rule` re-validates
 * like a create; `kind` switches — static -> dynamic needs a `rule` in the
 * same call and clears the list, dynamic -> static drops the rule and starts
 * from `memberIds` (nothing when absent). `memberIds` is legal ONLY on that
 * dynamic -> static switch, so it rides only beside `kind: 'static'`.
 */
export async function updateGroup(collectionId, groupId, { name, kind, addMemberIds, removeMemberIds, rule, memberIds, elementChoices }) {
	const payload = {};
	if (name !== undefined) payload.name = name;
	if (kind !== undefined) payload.kind = kind;
	if (addMemberIds?.length) payload.addMemberIds = addMemberIds;
	if (removeMemberIds?.length) payload.removeMemberIds = removeMemberIds;
	if (rule) payload.rule = ruleBody(rule);
	if (kind === 'static' && Array.isArray(memberIds)) payload.memberIds = memberIds;
	const { data } = await api.put(sharingUrl(collectionId, `groups/${groupId}`), withElementChoicesField(payload, elementChoices));
	return data;
}

export async function deleteGroup(collectionId, groupId) {
	await api.delete(sharingUrl(collectionId, `groups/${groupId}`));
}

/**
 * The group's current roster (manage-gated): a dynamic group's resolved live
 * — every account linked to an item its rule matches, existing and not
 * deactivated — a static group's stored list. `X-Total-Count` carries the
 * count; a header the client cannot read falls back to the list's length.
 * { members: [publicUser], totalCount }
 */
export async function getGroupMembers(collectionId, groupId) {
	const { data, headers } = await api.get(sharingUrl(collectionId, `groups/${groupId}/members`));
	const members = Array.isArray(data) ? data : [];
	const header = Number(headers?.['x-total-count']);
	return { members, totalCount: Number.isFinite(header) && header >= 0 ? header : members.length };
}

/**
 * Idempotent per (principal, card scope): re-granting updates the role.
 * card_id null/undefined = whole collection.
 *
 * `query` (KQL text) narrows a grant to the items it matches. The API accepts
 * it only with role 'read' and never together with card_id. This POST is a
 * COMPLETE statement of what the grant is, so an absent query CLEARS a filter
 * the grant used to carry.
 */
export async function saveGrant(collectionId, { principalType, principalId, role, card_id, query, elementChoices }) {
	const payload = { principalType, principalId, role };
	if (card_id) payload.card_id = card_id;
	if (query) payload.query = query;
	const { data } = await api.post(sharingUrl(collectionId, 'grants'), withElementChoicesField(payload, query ? elementChoices : null));
	return data;
}

/**
 * Unlike the upsert, this PUT is a partial statement: an ABSENT query leaves
 * the stored filter alone, `null` clears it. The stored filter still has to
 * survive the role change — raising a filtered grant to write is a 400, not a
 * silent widening — so callers raising the role must clear the query in the
 * same call.
 */
export async function updateGrant(collectionId, grantId, { role, query, elementChoices }) {
	const payload = { role };
	if (query !== undefined) payload.query = query;
	const { data } = await api.put(sharingUrl(collectionId, `grants/${grantId}`), withElementChoicesField(payload, query ? elementChoices : null));
	return data;
}

export async function deleteGrant(collectionId, grantId) {
	await api.delete(sharingUrl(collectionId, `grants/${grantId}`));
}

/**
 * Groups the caller may clone (collections they manage). Each row:
 * { _id, name, collection_id, collectionName, kind, memberCount } — a dynamic
 * group's count resolved live. A clone keeps the source's kind and copies its
 * rule, which the API re-validates in the TARGET collection (400
 * `invalid_rule` when the rule's card is not a member there).
 */
export async function getCloneableGroups() {
	const { data } = await api.get(API_CLONEABLE_GROUPS_URL);
	return data || [];
}

/**
 * The on-demand audience audit for one item. Read-gated like the item
 * itself; returns only the public user projection.
 * { owner, entries: [{type:'user'|'group', role, user?|group?+members}], includesAdmins }
 */
export async function getItemAudience(itemId) {
	const { data } = await api.get(`${API_ITEMS_URL}/${itemId}/audience`);
	return data;
}

/**
 * The elements of `cardId` the CALLER cannot change because a group built
 * from records reads them (owner ruling b79; GET …/cards/{cardId}/
 * locked-elements) → [{ name, when }], `when` being 'always' (create and
 * edit), 'edit' (an existing record only) or 'linked' (while the stored
 * record links an account). Names only: the API never says which group, or
 * what its rule is. Empty for a manager and for anyone who cannot write the
 * card.
 */
export async function getLockedElements(collectionId, cardId) {
	const { data } = await api.get(sharingUrl(collectionId, `cards/${cardId}/locked-elements`));
	return Array.isArray(data?.elements) ? data.elements : [];
}

/**
 * Who can make such a change: the collection's managers (and its parents'),
 * public projection — name and avatar, never email. Answered to anyone who
 * can edit something in the collection.
 */
export async function getCollectionManagers(collectionId) {
	const { data } = await api.get(sharingUrl(collectionId, 'managers'));
	return Array.isArray(data?.managers) ? data.managers : [];
}

/**
 * Invite someone who has no account yet (platform admins only; the API
 * answers 403 to anyone else). 202 { invite: { userId, email, expiresAt } }:
 * the userId is a shell account a grant can already name, and it is marked
 * `invited: true` on the grant's `user` until the invitee accepts.
 */
export async function inviteByEmail({ email, firstName, lastName }) {
	const { data } = await api.post(API_ADMIN_INVITES_URL, { email, firstName, lastName });
	return data.invite;
}
